import React, {ChangeEvent, useCallback, useMemo, useState} from "react";
import { useHistory } from "react-router-dom";
import styles from "./downloadUpdateHistoryModal.module.scss";
import Modal from "@components/UI/modal/modal";
import * as Yup from "yup";
import { useFormik } from "formik";
import RadioInput from "@components/UI/inputs/radioInput/radioInput";
import { DownloadFileType, DonwloadFileTypeDateRange, DonwloadFileTypeTranslate, IDownloadResidentUpdateHistoryRequest } from "@source/api/models/residentModel";
import moment from "moment";
import iconPdfUpdates from "@source/assets/icons/iconPdfUpdates.svg";
import iconDoc from "@source/assets/icons/iconDoc.svg";
import DateRangePicker from "@source/components/UI/inputs/dateRangePicker/dateRangePicker";
import { IFillVariant } from "@source/utils/enums";
import residentsStore from "@store/residentsStore";
import LoaderComponent from "@components/loader/loader";
import SelectInput, {ISelectItem} from "@components/UI/inputs/selectInput/selectInput";
import {SelectChangeEvent} from "@mui/material";
import nursingHomeStore from "@store/nursingHomeStore";

const DownloadUpdateHistoryModal = (props: DownloadUpdateHistoryModalProps) => {
  const { open, setOpen, onDownload, isLoading, fromStatisticPage } = props;
  const router = useHistory();

  const [filtersDirty, setFiltersDirty] = useState(false);
  
  const validateSchema = Yup.object({
    dateRangeOption: Yup.string().required("Option reason is required"),
    dateRange: Yup.array().nullable().required("Date is required"),
  });

  const mainFormik = useFormik({
    validationSchema: validateSchema,
    validateOnChange: true,
    validateOnBlur: true, 
    initialValues: {
      dateRangeOption: DonwloadFileTypeDateRange.FULL_HISTORY,
      dateRange: null,
    },
    onSubmit: () => {
    }
  });

  const getRadios = useCallback(() => {
    let radios = [
      {value: DonwloadFileTypeDateRange.FULL_HISTORY, label: "Full history"},
      {value: DonwloadFileTypeDateRange.DATE_RANGE, label: "Select date range"},
    ];

    return radios;
  }, [])

  const handleRadioChange = (e: ChangeEvent<HTMLInputElement>) => {
    mainFormik.setFieldValue("dateRangeOption", e.target.value);
  }

  const handleDateRange = (date: [Date, Date] | null) => {
    mainFormik.setFieldValue("dateRange", date);
  }

  const handleDownload = (downloadType: string) => {
    if (mainFormik.values.dateRangeOption === DonwloadFileTypeDateRange.DATE_RANGE && !mainFormik.values.dateRange) {
      mainFormik.handleSubmit();

      return;
    }

    const downloadUpdateHistoryReq: IDownloadResidentUpdateHistoryRequest = {
      residentId: residentsStore.activeResident?._id || "",
      type: downloadType,
      startDate: mainFormik.values.dateRange ? moment(mainFormik.values.dateRange[0]).format() : "",
      endDate: mainFormik.values.dateRange ? moment(mainFormik.values.dateRange[1]).format() : "",
    }

    if (mainFormik.values.dateRangeOption !== DonwloadFileTypeDateRange.DATE_RANGE) {
      delete downloadUpdateHistoryReq.startDate;
      delete downloadUpdateHistoryReq.endDate;
    }
    
    onDownload(downloadUpdateHistoryReq);
  }

  const selectNursingHomeItems: ISelectItem[] = useMemo(() => {
    return [
      {
        // @ts-ignore
        title: nursingHomeStore.activeNursingHome.name,
        // @ts-ignore
        value: nursingHomeStore.activeNursingHome._id,
        // @ts-ignore
        id: nursingHomeStore.activeNursingHome._id,
      },
      ...nursingHomeStore.nursingHomes?.filter((home) => home.name !== 'Demo Home' && home._id !== nursingHomeStore.activeNursingHome?._id)?.map((home) => {
        return { title: home.name, value: home._id, id: home._id }
      }) || []
    ];
  }, [nursingHomeStore.nursingHomes]);

  const [nursingHomeSelect, setNursingHomeSelect] = useState(selectNursingHomeItems[0].value);

  const onNursingHomeChange = useCallback((e: SelectChangeEvent) => {
    const selectedNursingHomeItem = selectNursingHomeItems.find((selectItem) => selectItem.value === e.target.value);

    if (selectedNursingHomeItem) {
      setNursingHomeSelect(selectedNursingHomeItem.value);
      setFiltersDirty(true);
    }
  }, [selectNursingHomeItems])

  const {errors, values, touched, handleBlur, handleChange, handleSubmit} = mainFormik;
  return(
    <Modal open={open} className={styles.downloadUpdateHistoryModal} close={setOpen} maxWidth={484}>
      <div className={styles.downloadUpdateHistory}>
        <span className={styles.title}>Download update history</span>

        <span className={`${styles.text} ${errors.dateRangeOption && styles.text_error}`}>
          Please select option.
        </span>

        <form className={styles.form} onSubmit={handleSubmit}>
          <RadioInput
            id="dateRangeOption" 
            name="dateRangeOption_radio"
            value={values.dateRangeOption}
            labelPlace="end"
            className={styles.radio_input}
            onChange={handleRadioChange}
            radios={getRadios()}
            touched={touched.dateRangeOption}
            error={errors.dateRangeOption} 
          />

          {
            values.dateRangeOption === DonwloadFileTypeDateRange.DATE_RANGE ?
              <DateRangePicker
                placeholder="Select date"
                className={styles.form_input} 
                value={values.dateRange} 
                onChange={handleDateRange} 
                fill={IFillVariant.Blank}
                maxDate={new Date()}
                touched={touched.dateRange}
                error={errors.dateRange}
              />
              :
              <></>
          }

          { fromStatisticPage &&
              <div style={{ marginTop: 25 }}>
                <SelectInput value={nursingHomeSelect}
                           className={styles.select}
                           handleChange={onNursingHomeChange} id="nursingHomeSelect"
                           items={selectNursingHomeItems}
                           label={'Home'}
                />
              </div>
          }

          <LoaderComponent isLoading={isLoading}>
          <div className={styles.downloadBtns}>
            <div className={styles.downloadFile} onClick={() => handleDownload(DownloadFileType.PDF)}>
              <div className={styles.left}>
                <img className={styles.icon} src={iconPdfUpdates} alt="pdf"/>
              </div>

              <div className={styles.right}>
                <span className={styles.text}>{DonwloadFileTypeTranslate[DownloadFileType.PDF]}</span>
              </div>
            </div>

              <div className={styles.downloadFile} onClick={() => handleDownload(DownloadFileType.DOCX)}>
                <div className={styles.left}>
                  <img className={styles.icon} src={iconDoc} alt="docx"/>
                </div>

                <div className={styles.right}>
                  <span className={styles.text}>{DonwloadFileTypeTranslate[DownloadFileType.DOCX]}</span>
                </div>
              </div>
          </div>
          </LoaderComponent>
        </form>
      </div>
    </Modal>
  )
}

export default DownloadUpdateHistoryModal;

interface DownloadUpdateHistoryModalProps {
  open: boolean,
  isLoading: boolean,
  fromStatisticPage?: boolean,
  setOpen: () => void,
  onDownload: (req: IDownloadResidentUpdateHistoryRequest) => void,
}

